import Component from '@glimmer/component';

import { concat, get, array } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import stopPropagation from 'ember-event-helpers/helpers/stop-propagation';

import CButton from 'rise-ui/components/c-button.gjs';
import CheckAllButton from 'rise-ui/components/check-all-button.gjs';
import CompactQuickFilter from 'rise-ui/components/compact-quick-filter.gjs';
import DateRangeDisplaySimple from 'rise-ui/components/date-range-display-simple.gjs';
import AddButton from 'rise-ui/components/time-series/selection/output/actions/add-button.gjs';
import Dates from 'rise-ui/components/time-series/selection/output/actions/dates.gjs';
import and from 'rise-ui/helpers/and.js';
import availableDates from 'rise-ui/helpers/available-dates.js';
import classMod from 'rise-ui/helpers/class-mod.js';
import concatClasses from 'rise-ui/helpers/concat-classes.js';
import dateUnbound from 'rise-ui/helpers/date-unbound.js';
import fnx from 'rise-ui/helpers/fnx.js';
import not from 'rise-ui/helpers/not.js';
import oneString from 'rise-ui/helpers/one-string.js';
import or from 'rise-ui/helpers/or.js';
import parameterTimestep from 'rise-ui/helpers/parameter-timestep.js';
import parameterTransformation from 'rise-ui/helpers/parameter-transformation.js';
import parameterUnit from 'rise-ui/helpers/parameter-unit.js';
import qpDateRangeInvalid from 'rise-ui/helpers/qp-date-range-invalid.js';
import qpDateRel from 'rise-ui/helpers/qp-date-rel.js';
import string from 'rise-ui/helpers/string.js';
import timeSeriesSelect from 'rise-ui/helpers/time-series-select.js';
import titleCase from 'rise-ui/helpers/title-case.js';
import attr from 'rise-ui/modifiers/attr.js';

export default class TimeSeriesSelectionLineItemComponent extends Component {
  @service('time-series/dates') dates;
  @service('time-series/selected-locations') selectedLocations;

  <template>
    {{#let (availableDates @item) as | availableDates |}}
    {{#let
      (and
        (not @item.hasAllModelRunDates)
        (or
          (dateUnbound availableDates.from availableDates.to @dateRanges)
          (qpDateRangeInvalid availableDates @dateRanges)
        )
      )
      as | isDateRangeInvalid |
    }}
    <div
      class={{concatClasses
        "c-time-series-selection-line c-lined__line"
        (if @item.isModeled
          "c-time-series-selection-line-modeled-item"
          (concatClasses
            "c-time-series-selection-line-item"
            (classMod "c-time-series-selection-output" @isValid "--valid")
          )
        )
      }}
      {{attr "data-qa-output" (not @item.isModeled) "observed"}}
      {{attr "data-qa-output-id" (not @item.isModeled) @item.id}}
      ...attributes
    >
      <div class="c-time-series-selection-line__wrapper">
        <span role="button"
          class={{classMod "c-time-series-selection-line-actions dummy-button"
            (not @item.isModeled) "--slim"
          }}
          {{on "click" (stopPropagation)}}
        >
          {{#if @item.isModeled}}
            <span class="c-time-series-selection-line-actions__filter">
              <CompactQuickFilter
                @compactTitle={{concat "Quick Filter " (titleCase (string "catalog-item"))}}
                @action={{fnx this.selectedLocations.quickFilterItem @item.id}}
              />
            </span>
            <span class="c-time-series-selection-line-actions__check-all">
              <CheckAllButton
                @classMix="c-time-series-selection-line-actions__check-all-button"
                @action={{fnx this.selectedLocations.checkAllModelRuns @item.id}}
                @isNoneChecked={{get this.selectedLocations.isNoModelRunSelected @item.id}}
                @isAllChecked={{get this.selectedLocations.isEveryModelRunSelected @item.id}}
                @checkText={{concat
                  "Select All "
                  (titleCase (oneString
                    @item.hasProfile "model-run-profile"
                    true "model-runs"
                  ))
                }}
                @uncheckText={{concat
                  "Deselect All "
                  (titleCase (oneString
                    @item.hasProfile "model-run-profile"
                    true "model-runs"
                  ))
                }}
              />
            </span>
          {{else}}
            <AddButton
              @name={{titleCase (string "catalog-item")}}
              @isSelected={{@isSelected}}
              @linkTo={{timeSeriesSelect toggleItem=@item.id}}
            />
          {{/if}}
        </span>
        {{#if (not @item.isModeled)}}
          <Dates @availableDates={{availableDates}}
            @dateRanges={{@dateRanges}}
            @isDateRangeInvalid={{isDateRangeInvalid}}
            @onDatesChange={{fnx this.dates.onItemDateChange @item.id}}
            @linkToClear={{timeSeriesSelect
              clearItemDates=@item.id
            }}
            @linkToCopy={{timeSeriesSelect
              addItem=@item.id
              copyDefaultDates=true
            }}
            @linkToWeek={{timeSeriesSelect
              addItem=@item.id
              setItemDates=(qpDateRel "week")
            }}
            @linkToMonth={{timeSeriesSelect
              addItem=@item.id
              setItemDates=(qpDateRel "month")
            }}
            @linkToYear={{timeSeriesSelect
              addItem=@item.id
              setItemDates=(qpDateRel "year")
            }}
            @linkTo10Years={{timeSeriesSelect
              addItem=@item.id
              setItemDates=(qpDateRel "decade")
            }}
            @linkToAll={{timeSeriesSelect
              addItem=@item.id
              setItemDates=(qpDateRel "all")
            }}
            @onDropdownSelection={{@onDateDropdownSelection}}
            @datesRequired={{and (not @item.isModeled) @datesRequired}}
          />
        {{/if}}
        <CButton @type="small muted" @external="true"
          class="c-time-series-selection-line-actions__item-link"
          @linkTo={{array "catalog.record.item" @item.catalogRecord.id @item.id}}
        >Item Details</CButton>
        <span class="c-time-series-selection-line__type">
          {{~titleCase (string "catalog-item")}}{{unless @item.isModeled " - Output"~}}
        </span>
        <span class="c-time-series-selection-line__label">
          {{#if @item.parameter}}
            {{titleCase (oneString
              @item.isActuallyModeled "item-modeled-value"
              true "item-observed-value"
            )}}
            {{parameterTimestep @item.parameter.parameterTimestep}}
            {{parameterTransformation @item.parameter.parameterTransformation}}
            {{@item.parameter.parameterName}}
            {{if @item.parameter.parameterUnit
              (concat "(" (parameterUnit @item.parameter.parameterUnit) ")")
            }}
          {{else}}
            {{@item.title}}
          {{/if}}
          {{#if (and
            (not @item.hasAllModelRunDates)
            @item.temporalStartDate
            @item.temporalEndDate
          )}}
            <DateRangeDisplaySimple
              title="Available Date Range"
              class="c-time-series-selection-line__section-dates"
              @fromDate={{@item.temporalStartDate}}
              @toDate={{@item.temporalEndDate}}
            />
          {{/if}}
        </span>
      </div>
    </div>
    {{/let}}
    {{/let}}
  </template>
}
